import { useMemo } from 'react'
import { BadgeCheck } from 'lucide-react'
import { FAKE_INFLUENCERS } from '../data/fakeInfluencers'
import './HeroInfluencerCollage.css'

// Seeded RNG (mulberry32). Not cryptographic — just stable per session.
function mulberry32(seed) {
    let t = seed >>> 0
    return () => {
        t = (t + 0x6d2b79f5) >>> 0
        let r = t
        r = Math.imul(r ^ (r >>> 15), r | 1)
        r ^= r + Math.imul(r ^ (r >>> 7), r | 61)
        return ((r ^ (r >>> 14)) >>> 0) / 4294967296
    }
}

function shuffle(arr, rand) {
    const out = arr.slice()
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1))
        ;[out[i], out[j]] = [out[j], out[i]]
    }
    return out
}

function formatFollowers(n) {
    if (n >= 10000) return `${Math.round(n / 1000)}K`
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
    return String(n)
}

// Spread cards across the hero in a rough grid with jitter so they don't overlap.
// Each card belongs to one of `rows` × `cols` cells and is offset within it.
function layoutCards(count, rand) {
    const rows = 4
    const cols = Math.ceil(count / rows)
    const cells = []
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) cells.push([r, c])
    }
    const picked = shuffle(cells, rand).slice(0, count)
    return picked.map(([r, c]) => {
        const jitterX = (rand() - 0.5) * 8
        const jitterY = (rand() - 0.5) * 8
        const top = (r / rows) * 100 + (100 / rows) * 0.2 + jitterY
        const left = (c / cols) * 100 + (100 / cols) * 0.15 + jitterX
        const rotate = (rand() - 0.5) * 4
        const driftX = (rand() - 0.5) * 40
        const driftY = (rand() - 0.5) * 30
        const duration = 28 + rand() * 20
        const delay = -rand() * duration
        return { top, left, rotate, driftX, driftY, duration, delay }
    })
}

function HeroInfluencerCollage({ desktopCount = 16, mobileCount = 6 }) {
    const { desktopCards, mobileCards } = useMemo(() => {
        const seed = Math.floor(Date.now() / 86400000)
        const rand = mulberry32(seed)
        const pool = shuffle(FAKE_INFLUENCERS, rand)
        const desktopPick = pool.slice(0, desktopCount)
        const mobilePick = pool.slice(0, mobileCount)
        const desktopLayouts = layoutCards(desktopCount, mulberry32(seed + 1))
        const mobileLayouts = layoutCards(mobileCount, mulberry32(seed + 2))
        return {
            desktopCards: desktopPick.map((card, i) => ({
                ...card,
                layout: desktopLayouts[i],
            })),
            mobileCards: mobilePick.map((card, i) => ({
                ...card,
                layout: mobileLayouts[i],
            })),
        }
    }, [desktopCount, mobileCount])

    return (
        <div className="hero-collage" aria-hidden="true">
            <div className="hero-collage__layer hero-collage__layer--desktop">
                {desktopCards.map((card) => (
                    <Card key={`d-${card.id}`} card={card} />
                ))}
            </div>
            <div className="hero-collage__layer hero-collage__layer--mobile">
                {mobileCards.map((card) => (
                    <Card key={`m-${card.id}`} card={card} />
                ))}
            </div>
            <div className="hero-collage__mask" />
        </div>
    )
}

function Card({ card }) {
    const { layout } = card
    const style = {
        top: `${layout.top}%`,
        left: `${layout.left}%`,
        '--drift-x': `${layout.driftX}px`,
        '--drift-y': `${layout.driftY}px`,
        '--rotate': `${layout.rotate}deg`,
        '--duration': `${layout.duration}s`,
        '--delay': `${layout.delay}s`,
    }
    return (
        <div className="hero-collage__card" style={style}>
            <img
                className="hero-collage__avatar"
                src={card.photo}
                alt=""
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                width={48}
                height={48}
            />
            <div className="hero-collage__meta">
                <div className="hero-collage__name">
                    <span>{card.name}</span>
                    {card.verified && (
                        <BadgeCheck size={12} className="hero-collage__check" aria-hidden="true" />
                    )}
                </div>
                <div className="hero-collage__handle">@{card.handle}</div>
                <div className="hero-collage__stats">
                    <span className="hero-collage__followers">{formatFollowers(card.followers)} followers</span>
                    <span className="hero-collage__niche">{card.niche}</span>
                </div>
            </div>
        </div>
    )
}

export default HeroInfluencerCollage
